import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, CalendarClock, RotateCcw, Sparkles } from 'lucide-react';
import { AssessmentResult } from '../../types';
import { AssessmentSummaryTiles, PriorityChip } from '../assessment/AssessmentCharts';
import { PriorityChart } from '../assessment/PriorityChart';
import { DashboardCard, DashboardEmptyState, DashboardSectionTitle } from './DashboardShared';
import { formatPersianDateWithTime } from '../../utils/dateUtils';
import { getCoursePathSegment } from '../../utils/coursePath';

const fa = (n: number) => n.toLocaleString('fa-IR');

export const DashboardGrowthPanel: React.FC<{
  result: AssessmentResult | null;
  /** پرسشنامه دعوت‌نامه‌ای است و این کاربر دعوت ندارد */
  locked?: boolean;
  /** ادمین شرکت مجدد را بسته است؛ دکمهٔ «پاسخ‌دهی مجدد» بی‌فایده است */
  allowRetake?: boolean;
}> = ({ result, locked, allowRetake = true }) => {
  if (!result) {
    // بدون دعوت‌نامه دکمهٔ شروع نمایش داده نمی‌شود، وگرنه کاربر به بن‌بست می‌خورد
    if (locked) {
      return (
        <DashboardEmptyState
          title="پرسشنامهٔ مسیر رشد برای شما فعال نیست"
          subtitle="این پرسشنامه فقط با لینک دعوت اختصاصی باز می‌شود. اگر در کارگاه شرکت کرده‌اید و لینکی دریافت نکرده‌اید، با پشتیبانی تماس بگیرید."
        />
      );
    }
    return (
      <DashboardEmptyState
        title="هنوز پرسشنامهٔ مسیر رشد را کامل نکرده‌اید"
        subtitle="با پاسخ به پرسشنامه، دوره‌ها بر اساس نیاز واقعی شما اولویت‌بندی می‌شوند."
        action={
          <Link
            to="/assessment"
            className="rounded-xl bg-[#1C3B9B] px-4 py-2 text-sm font-bold text-white shadow-[0_10px_22px_-14px_rgba(28,59,155,0.6)]"
          >
            شروع پرسشنامه
          </Link>
        }
      />
    );
  }

  const topThree = result.items.slice(0, 3);

  return (
    <div className="space-y-4">
      <div className="rounded-3xl border border-[#DDE5FF] bg-[linear-gradient(145deg,#FFFFFF_0%,#F7FAFF_100%)] p-4 shadow-[0_14px_30px_-20px_rgba(28,59,155,0.35)] sm:p-5">
        <DashboardSectionTitle
          title="مسیر رشد من"
          subtitle="امتیاز هر بخش از ۰ تا ۲۵ — هرچه کمتر، دیدن آن دوره برای شما فوری‌تر است."
          accent="violet"
          action={
            allowRetake ? (
              <Link
                to="/assessment?retake=1"
                className="inline-flex items-center gap-1.5 rounded-xl border border-[#C8D6FF] bg-white px-3 py-2 text-xs font-black text-[#1C3B9B] transition hover:-translate-y-0.5"
              >
                <RotateCcw className="h-3.5 w-3.5" />
                پاسخ‌دهی مجدد
              </Link>
            ) : undefined
          }
        />
        <AssessmentSummaryTiles {...result.summary} />
        <p className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] font-bold text-slate-500">
          <span className="flex items-center gap-1">
            <CalendarClock className="h-3.5 w-3.5" />
            {result.completedAt ? formatPersianDateWithTime(result.completedAt) : '—'}
          </span>
          {result.workshop ? <span>کارگاه: {result.workshop.title}</span> : null}
        </p>
      </div>

      {topThree.length ? (
        <DashboardCard className="border-[#DDE5FF]">
          <DashboardSectionTitle
            title="سه اولویت اول شما"
            subtitle="پیشنهاد می‌کنیم از این‌ها شروع کنید."
            accent="violet"
          />
          <div className="grid gap-2.5 sm:grid-cols-3">
            {topThree.map((item) => (
              <div
                key={item.sectionId}
                className="rounded-2xl border border-[#E6ECFA] bg-white p-3"
              >
                <div className="flex items-start justify-between gap-2">
                  <p className="min-w-0 flex-1 text-[13px] font-black text-slate-900">
                    {item.title}
                  </p>
                  <span className="shrink-0 text-[15px] font-black tabular-nums text-slate-900">
                    {fa(item.score25)}
                  </span>
                </div>
                <PriorityChip priority={item.priority} className="mt-2" />
                {item.course ? (
                  <Link
                    to={`/courses/${getCoursePathSegment(item.course)}`}
                    className="mt-2.5 flex items-center justify-between gap-2 rounded-xl bg-[#EEF3FF] px-3 py-2 text-[11.5px] font-black text-[#1C3B9B] transition hover:bg-[#E2EAFF]"
                  >
                    <span className="truncate">{item.course.title}</span>
                    <ArrowLeft className="h-3.5 w-3.5 shrink-0" />
                  </Link>
                ) : (
                  <p className="mt-2.5 rounded-xl bg-slate-50 px-3 py-2 text-[11px] font-bold text-slate-400">
                    دوره‌ای به این بخش متصل نشده است
                  </p>
                )}
              </div>
            ))}
          </div>
        </DashboardCard>
      ) : null}

      <DashboardCard className="border-[#DDE5FF]">
        <p className="mb-1 flex items-center gap-1.5 text-[12px] font-black text-slate-700">
          <Sparkles className="h-3.5 w-3.5 text-[#1C3B9B]" />
          نقشهٔ اولویت‌ها
        </p>
        <p className="mb-3 text-[10.5px] font-bold text-slate-400">
          امتیاز هر بخش روی محور ۰ تا ۲۵ — نقطهٔ راست‌تر یعنی فوری‌تر.
        </p>
        <PriorityChart items={result.items} />
      </DashboardCard>
    </div>
  );
};
